import { ChangeEvent, useCallback, useState } from 'react'

import { clsx } from 'clsx'

import { includesUrl } from '@bettermode/common/utils'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Link } from '@tribeplatform/react-ui-kit/Link'

import { T, useI18n } from '../i18n/index.js'

export const CUSTOM_MESSAGE_LIMIT = 1024

export const CustomMessageInput = ({
  register,
  errors,
  setValue,
  setExceedsLimit,
}) => {
  const { $t } = useI18n()
  const [showCustomMessage, setShowCustomMessage] = useState(false)
  const [customMessageLength, setCustomMessageLength] = useState(0)

  const close = () => {
    setValue('customMessage', '')
    setShowCustomMessage(false)
  }

  const handleCustomMessageChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const { length } = event.target.value
      setCustomMessageLength(length)
      setExceedsLimit(length >= CUSTOM_MESSAGE_LIMIT)
    },
    [setExceedsLimit],
  )

  if (showCustomMessage) {
    return (
      <div className="relative">
        <FormControl.Textarea
          invalid={!!errors?.customMessage?.message}
          label={$t({
            defaultMessage: 'Custom message',
            id: 'UserImport.CustomMessage',
          })}
          error={errors?.customMessage?.message}
          {...register('customMessage', {
            validate: value => {
              if (includesUrl(value)) {
                return $t({
                  defaultMessage:
                    'For security reasons, URLs are not allowed here',
                  id: 'UserImport.CustomInviteMessage.UrlNotAllowed',
                })
              }
              return true
            },
          })}
          onChange={handleCustomMessageChange}
          data-testid="custom-message-input"
        />
        <div
          className={clsx(
            'flex justify-end text-sm py-2',
            customMessageLength < CUSTOM_MESSAGE_LIMIT
              ? 'text-content-subdued '
              : 'text-action-destructive ',
          )}
        >
          {customMessageLength}/{CUSTOM_MESSAGE_LIMIT}
        </div>
        <div className="absolute top-0 end-0 cursor-pointer" onClick={close}>
          <SvgIcon
            className="w-5 h-5"
            onClick={close}
            data-testid="custom-message-remove"
            aria-label={$t({
              defaultMessage: 'Close',
              id: 'Generics.Close',
            })}
            name="close"
          />
        </div>
      </div>
    )
  }

  return (
    <FormControl>
      <FormControl.Label>
        <T defaultMessage="Custom message" id="UserImport.CustomMessage" />
      </FormControl.Label>
      <div className="text-sm text-content-subdued">
        <T
          defaultMessage="<link>Add a custom message</link> to the invitation"
          id="UserImport.CustomMessage.Description"
          values={{
            link: chunks => (
              <Link
                onClick={() => setShowCustomMessage(true)}
                as="button"
                type="button"
                data-testid="add-custom-message"
              >
                {chunks}
              </Link>
            ),
          }}
        />
      </div>
    </FormControl>
  )
}
